/** Default dietary-type icons — SVG data URLs for legacy seed values (metadata.icon = "veg", etc.). */
import { resolveAssetUrl } from './assetUrl';

function svgDataUrl(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg.trim())}`;
}

export const DIETARY_ICON_CODES = new Set([
  'veg',
  'non_veg',
  'vegan',
  'egg',
  'jain',
  'halal',
]);

export const DIETARY_DEFAULT_ICONS: Record<string, string> = {
  veg: svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect x="2" y="2" width="28" height="28" rx="2" fill="#fff" stroke="#16a34a" stroke-width="2.5"/>
      <circle cx="16" cy="16" r="7" fill="#16a34a"/>
    </svg>
  `),
  non_veg: svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect x="2" y="2" width="28" height="28" rx="2" fill="#fff" stroke="#92400e" stroke-width="2.5"/>
      <polygon points="16,8 24,24 8,24" fill="#dc2626"/>
    </svg>
  `),
  vegan: svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect x="2" y="2" width="28" height="28" rx="2" fill="#fff" stroke="#15803d" stroke-width="2.5"/>
      <path d="M16 22c-5-4-7-8-7-12a7 7 0 0 1 14 0c0 4-2 8-7 12z" fill="#22c55e"/>
      <path d="M16 10v12M13 13c2-2 5-2 6 0" stroke="#15803d" stroke-width="1.5" fill="none" stroke-linecap="round"/>
    </svg>
  `),
  egg: svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect x="2" y="2" width="28" height="28" rx="2" fill="#fff" stroke="#ca8a04" stroke-width="2.5"/>
      <ellipse cx="16" cy="17" rx="7" ry="9" fill="#fbbf24" stroke="#d97706" stroke-width="1.5"/>
    </svg>
  `),
  jain: svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect x="2" y="2" width="28" height="28" rx="2" fill="#fff" stroke="#16a34a" stroke-width="2.5"/>
      <circle cx="16" cy="16" r="7" fill="#16a34a"/>
      <path d="M11 16h10M16 11v10" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
    </svg>
  `),
  halal: svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect x="2" y="2" width="28" height="28" rx="2" fill="#fff" stroke="#059669" stroke-width="2.5"/>
      <circle cx="16" cy="16" r="9" fill="#10b981"/>
      <path d="M20 12a5 5 0 1 0-8 4" fill="#fff"/>
      <circle cx="19" cy="13" r="1.5" fill="#10b981"/>
    </svg>
  `),
};

function isUploadedAsset(value: string): boolean {
  const v = value.trim();
  return (
    v.startsWith('http://')
    || v.startsWith('https://')
    || v.startsWith('data:')
    || v.startsWith('/uploads/')
    || v.includes('/uploads/')
  );
}

/** True when metadata.icon stores a legacy code alias (e.g. "veg") instead of a file URL. */
export function isDietaryIconToken(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (!normalized || !DIETARY_ICON_CODES.has(normalized)) return false;
  return !isUploadedAsset(value);
}

export function resolveDietaryDefaultIconUrl(code: string): string {
  return DIETARY_DEFAULT_ICONS[code.trim().toLowerCase()] || '';
}

export function resolveDietaryIconUrl(
  code: string,
  rawIcon?: string,
): string {
  const normalizedCode = code.trim().toLowerCase();
  const fallback = DIETARY_DEFAULT_ICONS[normalizedCode] || '';
  const raw = String(rawIcon ?? '').trim();

  if (!raw) return fallback;

  if (isDietaryIconToken(raw)) {
    return DIETARY_DEFAULT_ICONS[raw.toLowerCase()] || fallback;
  }

  if (isUploadedAsset(raw)) return resolveAssetUrl(raw);

  return fallback;
}

export function resolveDietarySampleImageUrl(code: string): string | undefined {
  const url = resolveDietaryDefaultIconUrl(code);
  return url || undefined;
}

export function resolveDietaryListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  const raw = String(metadata?.icon ?? metadata?.imageUrl ?? metadata?.image ?? '').trim();
  const defaultIcon = resolveDietaryDefaultIconUrl(code);

  if (!raw) {
    return defaultIcon ? { primary: defaultIcon, fallback: '' } : { primary: '', fallback: '' };
  }

  if (isDietaryIconToken(raw)) {
    const resolved = DIETARY_DEFAULT_ICONS[raw.toLowerCase()] || defaultIcon;
    return { primary: resolved, fallback: '' };
  }

  if (isUploadedAsset(raw)) {
    return {
      primary: resolveAssetUrl(raw),
      fallback: defaultIcon,
    };
  }

  return defaultIcon
    ? { primary: defaultIcon, fallback: '' }
    : { primary: '', fallback: '' };
}
